import { useMutation, useQueryClient } from '@tanstack/react-query'
import { assert } from '@/utils'
import { traceIbcStatus2, IBCTransferStatus } from '@/wallet-utils'
import { queryKeys } from '@/query-keys'
import { MutationParameters } from './types'
import { useSelectedDenom } from '../../../../contexts/denom'

// [sign/broadcast] mutations and [trace] mutation are a 2-part process.
// First, if [sign/broadcast] doesn't succeed within the UI time limit, [trace] mutation will continue
// the [tracing] process so user can continue when the ibc transfer completes if they haven't closed the modal yet.
// We can use the `isLoading` status provided by [trace] mutation to display the "Return Later" screen.
const useTraceStakeAutopilotMutation = ({ stakeAutopilotTransaction }: MutationParameters) => {
  const client = useQueryClient()

  const [denom] = useSelectedDenom()

  const handleRequest = async (): Promise<{ status: IBCTransferStatus }> => {
    assert(stakeAutopilotTransaction, 'Unable to trace ibc status while transaction is empty.')

    const status = await traceIbcStatus2({ denom, tx: stakeAutopilotTransaction }, { timeout: false })

    return { status }
  }

  const handleSuccess = async () => {
    if (stakeAutopilotTransaction) {
      return await Promise.all([
        client.invalidateQueries({
          queryKey: queryKeys.transactionHistoryIbcStatusByHash({ hash: stakeAutopilotTransaction.transactionHash })
        }),
        client.invalidateQueries({ queryKey: queryKeys.chainBalancesBase })
      ])
    }

    await client.invalidateQueries({ queryKey: queryKeys.chainBalancesBase })
  }

  return useMutation({
    mutationFn: handleRequest,
    onSuccess: handleSuccess
  })
}

export { useTraceStakeAutopilotMutation }
